/**
 * LogManager Component
 * 
 * Displays circular buffer stats and provides log management actions
 * for the Debug Settings section in User Profile
 */

import React, { useState, useEffect } from 'react';
import { Flex, Text, Button, useToast, Icon } from '@chakra-ui/react';
import { getCurrentTraceId, getLogStats, getRawLogExport, resetSession } from '../../utils/circularBuffer';

interface LogStats {
  sizeInBytes: number;
  entryCount: number;
}

export function LogManager() {
  const [traceId, setTraceId] = useState<string | null>(null);
  const [stats, setStats] = useState<LogStats>({ sizeInBytes: 0, entryCount: 0 });
  const toast = useToast();

  // Load stats on mount and set up refresh interval
  useEffect(() => {
    const updateStats = () => {
      setTraceId(getCurrentTraceId());
      setStats(getLogStats());
    };

    updateStats();
    
    // Refresh stats every 5 seconds while component is mounted
    const interval = setInterval(updateStats, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const handleCopyTraceId = async () => {
    if (!traceId) return;
    
    try {
      await navigator.clipboard.writeText(traceId);
      toast({
        title: 'Trace ID Copied',
        description: 'Trace ID copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to copy trace ID:', error);
      toast({
        title: 'Copy Failed',
        description: 'Failed to copy trace ID',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleShareLogs = async () => {
    try {
      const rawLogs = getRawLogExport();
      
      if (!rawLogs) {
        toast({
          title: 'No Logs',
          description: 'No logs available to share',
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
        return;
      }

      await navigator.clipboard.writeText(rawLogs);
      toast({
        title: 'Logs Copied',
        description: `${stats.entryCount} log entries copied to clipboard`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to copy logs:', error);
      toast({
        title: 'Copy Failed',
        description: 'Failed to copy logs to clipboard',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleResetSession = () => {
    if (globalThis.confirm('Reset session? This will clear all logs and reload the page.')) {
      try {
        const newTraceId = resetSession();
        toast({
          title: 'Session Reset',
          description: `New session starting with trace ID: ${newTraceId}`,
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
        
        // Reload page after brief delay to show toast
        setTimeout(() => {
          globalThis.location.reload();
        }, 500);
      } catch (error) {
        console.error('Failed to reset session:', error);
        toast({
          title: 'Reset Failed',
          description: 'Failed to reset session',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      }
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 KB';
    const kb = bytes / 1024;
    return `${kb.toFixed(1)} KB`;
  };

  return (
    <>
      {/* Buffer Usage Status */}
      <Text fontSize="14px" color="#6B7280" mb={2}>
        Buffer Usage: {formatBytes(stats.sizeInBytes)} / 300 KB ({stats.entryCount} entries)
      </Text>

      {/* Trace ID Display */}
      {traceId && (
        <Flex align="center" gap={2} width="100%" mb={3}>
          <Text fontSize="14px" color="#6B7280" whiteSpace="nowrap">
            Trace ID:
          </Text>
          <Text 
            fontSize="12px" 
            fontFamily="monospace" 
            color="#374151"
            flex="1"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {traceId}
          </Text>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCopyTraceId}
            minW="auto"
            px={2}
            h="32px"
            fontSize="14px"
            color="#3B82F6"
            _hover={{ bg: 'gray.100' }}
            leftIcon={<Icon viewBox="0 0 24 24" boxSize="16px">
              <path 
                fill="currentColor" 
                d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
              />
            </Icon>}
          >
            Copy
          </Button>
        </Flex>
      )}

      {/* Action Buttons */}
      <Flex gap={2} width="100%">
        <Button
          flex="1"
          size="sm"
          h="32px"
          bg="#3B82F6"
          color="white"
          borderRadius="6px"
          fontSize="13px"
          onClick={handleShareLogs}
          _hover={{ bg: '#2563EB' }}
          _active={{ bg: '#1D4ED8' }}
        >
          Copy Logs
        </Button>
        
        <Button
          flex="1"
          size="sm"
          h="32px"
          bg="#EF4444"
          color="white"
          borderRadius="6px"
          fontSize="13px"
          onClick={handleResetSession}
          _hover={{ bg: '#DC2626' }}
          _active={{ bg: '#B91C1C' }}
        >
          Reset Session
        </Button>
      </Flex>
    </>
  );
}
